import { memo, useRef } from "react";
import { compareAccDocKey } from "../../decorators/StoreIdEquality";
import { AcceleratorBatchSource } from "../../models/AcceleratorBatchSource";
import { AcceleratorDocStoreKey } from "../../models/AcceleratorDoc";
import { DocumentStatus } from "../../types/DocumentStatuses";
import "../../styles/box.css";
import "../../styles/documentSelector/documentSelectorItem.css";
import Icon from "../Icon";
import { useAdvancedTooltip } from "../hooks/useAdvancedTooltip";
import { TTDocumentLoadingError } from "../tooltips/warnings/TTDocumentLoadingError";

const DocumentSelectorItemKeyProp = "data-accelerator-doc-id";
type Props = {
  onClick: (key: AcceleratorDocStoreKey) => void;
  /* Model Data */
  modelName: string;
  modelChanged: boolean;
  /* Batch Data */
  stale?: boolean;
  loadingError: boolean;

  acceleratorDocKey: AcceleratorDocStoreKey;
  statuses: DocumentStatus[];

  pages: number;
  docType: string;
  /**
   * Selected AcceleratorDocKey
   */
  selectedAccDocKey?: AcceleratorDocStoreKey;
};

const DocumentSelectorItem = (props: Props) => {
  const docStatuses = props.statuses;
  const colourToShow = docStatuses[0] ?? null;

  const selected = compareAccDocKey(
    props.selectedAccDocKey,
    props.acceleratorDocKey,
  );

  return (
    <div
      className={`box documentSelectorItem ${selected ? "selected" : ""} ${props.stale ? "stale" : ""}`}
      data-document-status={colourToShow ?? ""}
      // data-accelerator-doc-id={str_key}
      onClick={() => props.onClick(props.acceleratorDocKey)}
    >
      <div className={"box-section left"}>
        <strong>
          {/* Populated by DocumentSelectorList after all have been mounted */}
        </strong>
      </div>
      <div className={"box-inner"}>
        <p data-section="docType">
          {props.docType === "Unknown" ? "" : props.docType + ":"}
        </p>
        <p data-section="modelName">{props.modelName}</p>
        <p data-section="pages">
          {props.pages} {`Page${props.pages == 1 ? "" : "s"}`}
        </p>
      </div>
      <div className={"box-section right column"}>
        <Status {...props} />
      </div>
    </div>
  );
};

type StatusProps = {
  modelChanged: boolean;
  loadingError: boolean;
};
const Status = ({ modelChanged, loadingError }: StatusProps) => {
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  const tooltip = useAdvancedTooltip({
    noLongText: true,
    containerRef: triggerRef,
    tooltipText: TTDocumentLoadingError,
    possiblePlacements: ["left-center"],
  });

  if (loadingError) {
    return (
      <>
        <span
          {...tooltip.triggerProps}
          ref={triggerRef}
          data-loading-error
          className={"circle yellow"}
        >
          <Icon altText={""} icon={"exclamation-triangle"} />
        </span>
        {tooltip.isOpen && tooltip.renderTooltip()}
      </>
    );
  } else if (modelChanged) {
    return (
      <span
        data-model-changed
        className={"circle"}
        style={{
          float: "right",
          marginRight: 10,
        }}
      >
        {"Changed"}
      </span>
    );
  } else {
    return null;
  }
};

function propsEqual_docStauses(
  p: DocumentStatus[],
  n: DocumentStatus[],
): boolean {
  return p.length === n.length && p.every((value) => n.includes(value));
}

function propsEqual_DocumentSelectorItem(p: Props, n: Props): boolean {
  // sort equality checks by frequency/importance
  return (
    compareAccDocKey(p.selectedAccDocKey, n.selectedAccDocKey) &&
    compareAccDocKey(p.acceleratorDocKey, n.acceleratorDocKey) &&
    propsEqual_docStauses(p.statuses, n.statuses) &&
    p.docType === n.docType &&
    p.modelName === n.modelName &&
    p.modelChanged === n.modelChanged &&
    p.pages === n.pages &&
    p.stale == n.stale &&
    p.loadingError == n.loadingError
  );
}

const MemoizedDocumentSelectorItem = memo(
  DocumentSelectorItem,
  propsEqual_DocumentSelectorItem,
);

export {
  MemoizedDocumentSelectorItem as DocumentSelectorItem,
  DocumentSelectorItemKeyProp,
};
